import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { 
  Character, 
  CharacterFeatureSource, 
  KnowledgeFeature, 
  LearnableFeature,
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";

import CenteredMenu from "@components/input/menus/CenteredMenu";
import LoadingBox from "@components/input/LoadingBox";
import CharacterKnowledgeForUsage from "@dndComponents/character/knowledge/CharacterKnowledgeForUsage";

interface Props {
  character: Character; 
  onChange: () => void;
}

const common_util = CommonUtilities.getInstance();

/**
 * For each usage_key in their character knowledge, give a tab.
 * 
 * In each of those, show any knowledge from Knowledge Features with source name, 
 * clickable to pull up the CFS for it.
 * 
 * Also in each tab, if they have a Learnable Feature for it, then give controls
 * for adding to it.
 */
export default function CharacterKnowledgeMainInput({
  character,
  onChange,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [usages, setUsages] = useState<string[]>([]);
  const [selected_usage, setSelectedUsage] = useState("");

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  });
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const load = () => {
    const knowledge_features = character.knowledge_features;
    const knowledge_feature_usages = knowledge_features.map(o => {
      if (o.floating_feature instanceof KnowledgeFeature) {
        return o.floating_feature.usage_key;
      } 
      return "";
    });
    const learnable_features = character.learnable_features;
    const learnable_feature_usages = learnable_features.map(o => {
      if (o.floating_feature instanceof LearnableFeature) {
        return o.floating_feature.usage_key;
      } 
      return "";
    });

    const spell_usages = character.spells.filter(o => 
      o.knowledge_usage_key !== "" && 
      o.knowledge_type !== "None").map(o => o.knowledge_usage_key);
    const spell_as_ability_usages = character.spell_as_abilities.filter(o => 
      o.knowledge_usage_key !== "" && 
      o.knowledge_type !== "None").map(o => o.knowledge_usage_key);
    const new_usages = common_util.unique_array([
      ...knowledge_feature_usages,
      ...learnable_feature_usages,
      ...spell_usages,
      ...spell_as_ability_usages,
    ]).filter(o => o !== "");
    setUsages(new_usages);
    if (new_usages.length > 0) {
      setSelectedUsage(new_usages[0]);
    }
  }

  if (!fetched) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <CenteredMenu
            options={usages}
            selected={selected_usage}
            onSelect={(changed: string) => {
              setSelectedUsage(changed);
              setReloading(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { !reloading &&
            <CharacterKnowledgeForUsage 
              character={character}
              selected_usage={selected_usage}
              onChange={onChange}
            />
          }
        </Grid>
      </Grid>
    );
  }
}
